// Facility team chat (0087) — a per-GROUP message thread so a campus/team can
// talk about what's going on, ask questions, and flag issues. The group picker
// lists the groups the current user can chat in (members see their groups,
// admins see all — gated server-side). The thread renders oldest-at-top like
// chat, with a composer that posts and refreshes.

import { useEffect, useRef, useState } from "react";
import { listFacilityGroupMessages, listFacilityGroups, postFacilityGroupMessage } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate } from "../lib/format";
import type { FacilityGroupMessageRow } from "../lib/types";
import { PageBar } from "../components/Layout";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";

function initials(name?: string | null): string {
  if (!name) return "•";
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "•"
  );
}

export function FacilityChatPage() {
  const [groupId, setGroupId] = useState<string>("");
  const groupsApi = useApi((s) => listFacilityGroups(s), []);
  const groups = groupsApi.data?.groups ?? [];

  // Default to the first group once they load.
  useEffect(() => {
    if (!groupId && groups.length) setGroupId(groups[0]!.id);
  }, [groups, groupId]);

  return (
    <>
      <PageBar crumbs={<>Facilities <span aria-hidden="true">/</span> Team Chat</>} />
      <div className="content">
        <div className="card">
          <div className="card-head">
            <h3>Team Chat</h3>
            {groups.length > 1 ? (
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)} aria-label="Group">
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <div className="card-body">
            {groupsApi.loading ? (
              <Loading label="Loading groups…" />
            ) : groupsApi.error ? (
              <ErrorState error={groupsApi.error} onRetry={groupsApi.reload} />
            ) : groups.length === 0 ? (
              <EmptyState>
                No team groups yet. An admin can create facility groups in Settings, then members can chat here.
              </EmptyState>
            ) : groupId ? (
              <GroupChatThread groupId={groupId} />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

/** The message thread + composer for one group. Re-mounts on group change. */
function GroupChatThread({ groupId }: { groupId: string }) {
  const [messages, setMessages] = useState<FacilityGroupMessageRow[]>([]);
  const [body, setBody] = useState("");
  const { data, loading, error, reload } = useApi((s) => listFacilityGroupMessages(groupId, s), [groupId]);
  const { run, pending, error: postError } = useMutation((text: string) => postFacilityGroupMessage(groupId, text));
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages(data?.messages ?? []);
  }, [data]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages]);

  const empty = body.trim() === "";

  async function post() {
    if (empty) return;
    const res = await run(body.trim());
    if (res) {
      setBody("");
      await reload();
    }
  }

  if (loading) return <Loading label="Loading messages…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="task-activity">
      {messages.length === 0 ? (
        <p className="hint" style={{ margin: "0 0 10px" }}>
          No messages yet. Start the conversation — share an update, ask a question, or flag an issue for the team.
        </p>
      ) : (
        <ul className="comment-list">
          {messages.map((m) => (
            <li key={m.id} className="comment">
              <span className="avatar xs" title={m.author_name ?? ""}>{initials(m.author_name)}</span>
              <div className="comment-body">
                <div className="comment-meta">
                  <strong>{m.author_name ?? "System"}</strong>
                  <span className="faint">{formatDate(m.created_at)}</span>
                </div>
                <div className="comment-text" style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
              </div>
            </li>
          ))}
          <div ref={endRef} />
        </ul>
      )}

      {postError ? <Alert kind="error">{postError}</Alert> : null}
      <div className="comment-composer">
        <textarea
          style={{ flex: 1, minWidth: 0 }}
          rows={2}
          value={body}
          disabled={pending}
          placeholder="Write a message…  (⌘/Ctrl+Enter to send)"
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") post();
          }}
        />
        <button className="btn primary" onClick={post} disabled={pending || empty}>
          {pending ? <Spinner label="Sending…" /> : "Send"}
        </button>
      </div>
    </div>
  );
}
